import { Link } from "@tanstack/react-router";
import { Facebook, Instagram } from "lucide-react";

import { useRulebookUrl } from "@/hooks/settings";

const linkClassName =
  "text-navbar-foreground hover:text-gray-400 transition-colors";

function TikTokIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <title>TikTok</title>
      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
  );
}

const socialSections = [
  {
    title: "CBVA",
    links: [
      {
        name: "CBVA Instagram",
        href: "https://www.instagram.com/thecbva",
        icon: Instagram,
      },
      {
        name: "CBVA Facebook",
        href: "https://www.facebook.com/theCBVA/",
        icon: Facebook,
      },
      {
        name: "CBVA TikTok",
        href: "https://tiktok.com/@thecbva",
        icon: TikTokIcon,
      },
    ],
  },
  {
    title: "Juniors",
    links: [
      {
        name: "CBVA Juniors Instagram",
        href: "https://www.instagram.com/cbvajuniors",
        icon: Instagram,
      },
      {
        name: "CBVA Juniors Facebook",
        href: "https://www.facebook.com/cbvajuniors",
        icon: Facebook,
      },
      {
        name: "CBVA Juniors TikTok",
        href: "https://tiktok.com/@thecbva",
        icon: TikTokIcon,
      },
    ],
  },
];

export function Footer() {
  const rulebookUrl = useRulebookUrl();
  return (
    <footer className="bg-navbar-background text-navbar-foreground mt-auto w-full px-3 py-5 md:px-6 md:py-10">
      <div className="mx-auto max-w-6xl">
        {/* Main row: stacked on small, side-by-side on lg */}
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-12">
          {/* Left side: logo + contact/social — centered on small, left-aligned on lg */}
          <div className="flex flex-col items-center gap-6 lg:flex-row lg:items-start">
            {/* Logo */}
            <Link
              to="/"
              className="shrink-0 transition-all hover:brightness-75"
            >
              <img
                alt="CBVA Bear"
                src="/logos/cbva-bear.svg"
                className="w-48"
              />
            </Link>

            {/* Contact + Social in a row */}
            <div className="flex flex-wrap justify-center gap-x-10 gap-y-4 lg:justify-start">
              <div className="text-center lg:text-left">
                <h3 className="mb-2 text-sm font-bold tracking-wide uppercase">
                  Contact
                </h3>
                <a
                  href="mailto:info@cbva.com"
                  className={`${linkClassName} text-sm`}
                >
                  info@cbva.com
                </a>
              </div>

              {socialSections.map((section) => (
                <div key={section.title} className="text-center lg:text-left">
                  <h3 className="mb-2 text-sm font-bold tracking-wide uppercase">
                    {section.title}
                  </h3>
                  <div className="flex justify-center gap-3 lg:justify-start">
                    {section.links.map(({ name, href, icon: Icon }) => (
                      <a
                        key={name}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={name}
                        className="text-navbar-foreground transition-colors hover:text-gray-400"
                      >
                        <Icon size={20} />
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right side: grouped link lists */}
          <div className="flex flex-wrap justify-center gap-8 text-sm lg:ml-auto lg:justify-end">
            <div>
              <h3 className="mb-3 text-sm font-bold tracking-wide uppercase">
                Tournaments
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/tournaments" className={linkClassName}>
                    Upcoming
                  </Link>
                </li>
                <li>
                  <Link to="/ratings" className={linkClassName}>
                    Ratings
                  </Link>
                </li>
                <li>
                  <Link
                    to="/leaderboard/{-$gender}"
                    params={{ gender: "womens" }}
                    className={linkClassName}
                  >
                    Leaderboard
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="mb-3 text-sm font-bold tracking-wide uppercase">
                Information
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/faqs/{-$subject}" className={linkClassName}>
                    FAQs
                  </Link>
                </li>
                <li>
                  <Link to="/prize-pool" className={linkClassName}>
                    Prize Pool
                  </Link>
                </li>
                <li>
                  <Link to="/juniors" className={linkClassName}>
                    Juniors
                  </Link>
                </li>
                {rulebookUrl && (
                  <li>
                    <a
                      href={rulebookUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClassName}
                    >
                      Rulebook
                    </a>
                  </li>
                )}
                <li>
                  <a
                    href="https://bvbhof.com/index.asp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClassName}
                  >
                    Hall of Fame
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="mb-3 text-sm font-bold tracking-wide uppercase">
                Account
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/log-in" className={linkClassName}>
                    Log In
                  </Link>
                </li>
                <li>
                  <Link to="/sign-up" className={linkClassName}>
                    Sign Up
                  </Link>
                </li>
                <li>
                  <Link to="/search" className={linkClassName}>
                    Search Players
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Copyright & Legal */}
        <div className="border-navbar-border mt-8 border-t pt-6 text-center text-sm opacity-75">
          <p>&copy; {new Date().getFullYear()} CBVA. All rights reserved.</p>
          <p className="mt-2">
            <Link to="/privacy-policy" className={linkClassName}>
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
